import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useAuth } from "../context/authContext";
import Footer from "../components/footer";
// Logo is in public folder for production builds
const logo = "/logoblue.png";

export default function Login() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const user = await login(identifier, password);
      console.log("Login successful, user:", user);
      // Navigation will happen via useEffect when isAuthenticated becomes true
      // But also navigate directly as backup
      if (user) {
        navigate("/", { replace: true });
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Login failed. Please check your credentials.");
      setLoading(false);
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F0F8FF",
        }}
      >
        <CircularProgress sx={{ color: "#87CEEB" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#FFFFFF",
        backgroundImage:
          "url('/bluebg.jpg'), " +
          "radial-gradient(circle at top, rgba(135,206,235,0.18), transparent 60%), " +
          "linear-gradient(135deg, #FFFFFF 0%, #F0F8FF 40%, #E6F3FF 70%, #FFFFFF 100%)",
        backgroundRepeat: "no-repeat, no-repeat, no-repeat",
        backgroundSize: "cover, 140% 140%, cover",
        backgroundPosition: "center center, top center, center center",
        backgroundBlendMode: "normal, soft-light, normal",
      }}
    >
      {/* Centered login card */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={10}
            sx={{
              p: 4,
              bgcolor: "rgba(255,255,255,0.96)",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "#87CEEB",
              boxShadow: "0 0 25px rgba(135,206,235,0.18)",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <img
                  src={logo}
                  alt="TSG AI"
                  style={{
                    height: "60px",
                    width: "auto",
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: "black" }}>
                Sign in with your Skillgenie email or username
              </Typography>
            </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email or Username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              sx={{ mb: 2 }}
              autoComplete="username"
              InputProps={{
                sx: { color: "black" },
              }}
              InputLabelProps={{
                sx: { color: "grey.700" },
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
              autoComplete="current-password"
              InputProps={{
                sx: { color: "black" },
              }}
              InputLabelProps={{
                sx: { color: "grey.700" },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: "#87CEEB",
                color: "#FFFFFF",
                fontWeight: 600,
                py: 1.5,
                "&:hover": {
                  bgcolor: "#00BFFF",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : "Sign In"}
            </Button>
          </form>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: "grey.600" }}>
              Use your Skillgenie credentials to access TSG AI.
            </Typography>
          </Box>
          </Paper>
        </Container>
      </Box>

      {/* Footer for login page */}
      <Footer />
    </Box>
  );
}

